import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, ensureCsrf } from "./api";
import type { User } from "./types";

interface AuthValue {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string, otp?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasPermission: (code: string) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const currentScope = useRef("");
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const updateUser = useCallback((nextUser: User | null) => {
    const scope = nextUser ? JSON.stringify([nextUser.id, nextUser.organization, nextUser.work_profile, nextUser.furnitura_access, [...nextUser.permissions].sort()]) : "";
    if (currentScope.current !== scope) {
      // A new account or changed permissions must never reuse the previous user's documents.
      queryClient.clear();
      currentScope.current = scope;
    }
    setUser(nextUser);
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    try { updateUser(await api<User>("/auth/me/")); }
    catch { updateUser(null); }
  }, [updateUser]);

  useEffect(() => {
    ensureCsrf().then(refreshUser).finally(() => setLoading(false));
  }, [refreshUser]);

  const value = useMemo<AuthValue>(() => ({
    user,
    loading,
    login: async (username, password, otp = "") => {
      await ensureCsrf();
      updateUser(await api<User>("/auth/login/", { method: "POST", body: JSON.stringify({ username, password, otp }) }, false));
    },
    logout: async () => { await api<void>("/auth/logout/", { method: "POST" }); updateUser(null); },
    refreshUser,
    hasPermission: (code) => Boolean(user && (code.startsWith("furnitura.") ? user.furnitura_access === true && user.permissions.includes("furnitura.access") : user.permissions.includes("*") || user.permissions.includes(code))),
  }), [user, loading, refreshUser, updateUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider is missing");
  return value;
}
